// Pub crawl settings, in one place. Walking is estimated from straight-line
// distance (no routing API): distance × a detour factor at WALK_SPEED_KMH.

/** Streets aren't straight: a town walk is ~1.3× the crow-flies distance... */
export const DETOUR_BUILT_UP = 1.3;
/** ...and lanes and footpaths between villages wander more. */
export const DETOUR_RURAL = 1.4;
/**
 * A leg counts as built-up when at least BUILT_UP_MIN_PUBS pubs (any pubs,
 * not just candidates) are within BUILT_UP_RADIUS_KM of its midpoint: about
 * 4 pubs per km², a town or a big village centre. Pub density is the
 * urban/rural signal already in the data.
 */
export const BUILT_UP_RADIUS_KM = 0.5;
export const BUILT_UP_MIN_PUBS = 3;
/** A steady pub-crawl pace, km/h (slower than a brisk 5). */
export const WALK_SPEED_KMH = 4.8;

export const MIN_STOPS = 3;
export const MAX_STOPS = 10;
export const DEFAULT_STOPS = 5;

/** Choices for the longest walk between two stops (minutes). */
export const LEG_STEPS = [10, 15, 20, 30, 45, 60] as const;
export const DEFAULT_MAX_LEG = 15;
/** Choices for the longest total walk (minutes); null = no limit. */
export const TOTAL_STEPS = [null, 30, 45, 60, 90, 120] as const;

export const DWELL_STEPS = [30, 45, 60] as const;
export const DEFAULT_DWELL = 45;
/** Start times are offered in steps of this many minutes. */
export const START_STEP_MIN = 15;

/** Never look further than this from the start (straight line, km). */
export const MAX_RADIUS_KM = 6;
/** Weather is fetched for at most this many pubs nearest the start... */
export const PRESELECT_PUBS = 60;
/** ...and one crawl spends at most this many Open-Meteo calls (it may dip into the reserve). */
export const CRAWL_MAX_CALLS = 20;

/**
 * Candidates the exact search runs over, by number of stops: the search is
 * exhaustive (branch and bound) over this pruned set, so it shrinks as the
 * crawl gets longer. Keeps the worst case well under the 300 ms target.
 */
export const SEARCH_CANDIDATES: Record<number, number> = {
  2: 30,
  3: 30,
  4: 24,
  5: 20,
  6: 18,
  7: 16,
  8: 15,
  9: 14,
  10: 13,
};
/** A safety stop for the search (never hit in testing); the result is then flagged inexact. */
export const SEARCH_NODE_LIMIT = 4_000_000;

/** "Best gardens": points added for confirmed outside space at a stop. */
export const CONFIRMED_BONUS = 10;
/** "Best gardens": points taken off where OSM says there's no outside space. */
export const NO_OUTSIDE_PENALTY = 15;
/** "Best gardens": score points one extra minute of walking has to be worth. */
export const WALK_POINTS_PER_MIN = 0.4;

/**
 * Better start times: crawls starting at these UK hours today and tomorrow
 * (only future ones, at most START_ALTERNATIVES of them, nearest the chosen
 * start first) are planned from the same pubs and weather, and one is
 * suggested if it scores at least START_MIN_GAIN more, or avoids dark stops
 * without scoring less.
 */
export const START_HOURS = [12, 13, 14, 15, 16, 17, 18] as const;
export const START_ALTERNATIVES = 6;
export const START_MIN_GAIN = 5;
/** Alternatives within this many minutes of the chosen start aren't worth suggesting. */
export const START_MIN_SHIFT_MIN = 45;

/** Rural legs longer than this (minutes) that end after dark get a torch warning. */
export const DARK_LEG_MIN = 10;
/** "Dark": the sun more than 6° below the horizon (civil dusk), as elsewhere in the app. */
export const DARK_SUN_ALTITUDE = -6;

/** Google Maps directions links: waypoints allowed (mobile browsers take 3, desktop 9). */
export const MAPS_WAYPOINTS_MOBILE = 3;
export const MAPS_WAYPOINTS_DESKTOP = 9;

/** Under every crawl, and on its share card. */
export const SENSIBLE_LINE = 'Know your limits and plan your way home.';

/** Walking minutes from a straight-line distance in km. */
export const walkMinutes = (km: number, detour: number) => ((km * detour) / WALK_SPEED_KMH) * 60;
/** Straight-line km you can cover in this many walking minutes (on the straightest streets). */
export const reachKm = (minutes: number) =>
  ((minutes / 60) * WALK_SPEED_KMH) / Math.min(DETOUR_BUILT_UP, DETOUR_RURAL);
